import React, { useState, useEffect, memo, useCallback, useRef, useMemo } from 'react';
import { 
  Button, Input, Select, Space, Typography, notification, Collapse, 
  Divider, Row, Col, Splitter, theme, Checkbox, InputNumber, Switch, Card, Tag, Popover
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined,
  UpOutlined, DownOutlined, CaretRightOutlined, SettingOutlined
} from '@ant-design/icons';
import { PARAMETRABLE_FIELD_TYPES } from './utils/constants';
import RichContentEditor from '../editor/RichContentEditor';
import FormPreview from './FormPreview.component';
import { AnswerEditor } from './common/AnswerEditor';
import { OtherOption } from './common/OtherOption';
import { CheckboxWithSelectItem } from './common/CheckboxWithSelectItem';
import { ActionButtons } from './common/ActionButtons';
import DependencyEditor from './common/DependencyEditor';
import DebouncedInput from './common/DebouncedInput';
import { 
  TERMINOLOGY_SYSTEMS, 
  DEFAULT_SNOMED_SYSTEM, 
  DEFAULT_ANSWER,
  DEFAULT_CHECKBOX_SELECT_ITEM,
  ANSWER_FIELD_TYPES
} from './utils/constants';
import { 
  renderTerminologySystemSelect, 
  renderDeleteButton, 
  renderTerminologySelect 
} from './utils/renderHelpers';
import { 
  getQuestion, 
  getSection, 
  getPage, 
  getAnswer, 
  getAnswers, 
  getSections, 
  getQuestions
} from './utils/helpers';
import { createUpdateSchema, createUpdateQuestionField } from './utils/schemaUpdater';
import { createHandlers } from './utils/handlers';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * Scale settings popover with support for custom marks (ratings/hybrid scales)
 */
const ScaleSettingsPopover = ({ config, onChange }) => {
  const useCustomMarks = config.useCustomMarks || false;
  const customMarks = config.customMarks || [];
  
  const updateConfig = (updates) => onChange({ ...config, ...updates });
  
  const addMark = () => {
    const maxPos = config.maxPosition || 100;
    const newPos = customMarks.length > 0 ? Math.min(maxPos, Math.max(...customMarks.map(m => m.position)) + 25) : 0;
    updateConfig({ customMarks: [...customMarks, { position: newPos, label: '', value: '' }] });
  };
  
  const updateMark = (index, field, val) => {
    const updated = [...customMarks];
    updated[index] = { ...updated[index], [field]: val };
    updateConfig({ customMarks: updated });
  };
  
  const removeMark = (index) => {
    updateConfig({ customMarks: customMarks.filter((_, i) => i !== index) });
  };

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <Space direction="vertical" size={8} style={{ width: 280 }}>
          <Text strong style={{ fontSize: 12 }}>Scale Settings</Text>
          <Checkbox
            checked={useCustomMarks}
            onChange={(e) => updateConfig({ 
              useCustomMarks: e.target.checked,
              customMarks: e.target.checked && customMarks.length === 0 
                ? [{ position: 0, label: 'Min', value: '0' }, { position: config.maxPosition || 100, label: 'Max', value: String(config.maxPosition || 100) }] 
                : customMarks 
            })}
            style={{ fontSize: 12 }}
          >
            Use custom marks (ratings/hybrid)
          </Checkbox>
          {!useCustomMarks ? (
            <>
              <Space size={8}>
                <span style={{ fontSize: 12 }}>Min:</span>
                <InputNumber size="small" value={config.min ?? 0} onChange={(v) => updateConfig({ min: v })} style={{ width: 60 }} />
                <Input size="small" value={config.minLabel || ''} onChange={(e) => updateConfig({ minLabel: e.target.value })} placeholder="Label" style={{ width: 70 }} />
              </Space>
              <Space size={8}>
                <span style={{ fontSize: 12 }}>Max:</span>
                <InputNumber size="small" value={config.max ?? 10} onChange={(v) => updateConfig({ max: v })} style={{ width: 60 }} />
                <Input size="small" value={config.maxLabel || ''} onChange={(e) => updateConfig({ maxLabel: e.target.value })} placeholder="Label" style={{ width: 70 }} />
              </Space>
              <Space size={8}>
                <span style={{ fontSize: 12 }}>Step:</span>
                <InputNumber size="small" value={config.step ?? 1} min={1} onChange={(v) => updateConfig({ step: v })} style={{ width: 60 }} />
              </Space>
              <Checkbox
                checked={config.showMarks !== false}
                onChange={(e) => updateConfig({ showMarks: e.target.checked })}
                style={{ fontSize: 12 }}
              >
                Show marks
              </Checkbox>
            </>
          ) : (
            <>
              <Space size={8}>
                <span style={{ fontSize: 12 }}>Max Position:</span>
                <InputNumber size="small" value={config.maxPosition ?? 100} min={1} onChange={(v) => updateConfig({ maxPosition: v })} style={{ width: 60 }} />
              </Space>
              <Space size={8}>
                <span style={{ fontSize: 12 }}>Step:</span>
                <InputNumber size="small" value={config.step ?? 1} min={1} onChange={(v) => updateConfig({ step: v })} style={{ width: 60 }} />
              </Space>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Pos (0-{config.maxPosition ?? 100}) | Label | Output</Text>
                {customMarks.map((mark, i) => (
                  <Space key={i} size={4}>
                    <InputNumber size="small" value={mark.position} min={0} max={config.maxPosition ?? 100} onChange={(v) => updateMark(i, 'position', v)} style={{ width: 50 }} placeholder="Pos" />
                    <Input size="small" value={mark.label} onChange={(e) => updateMark(i, 'label', e.target.value)} style={{ width: 70 }} placeholder="Label" />
                    <Input size="small" value={mark.value} onChange={(e) => updateMark(i, 'value', e.target.value)} style={{ width: 60 }} placeholder="Value" />
                    <Button type="text" size="small" danger onClick={() => removeMark(i)} style={{ padding: '0 4px' }}>×</Button>
                  </Space>
                ))}
                <Button type="dashed" size="small" onClick={addMark} block style={{ fontSize: 11 }}>+ Add Mark</Button>
              </Space>
            </>
          )}
        </Space>
      }
    >
      <Button icon={<SettingOutlined />} style={{ color: '#8c8c8c' }} />
    </Popover>
  );
};

// Rendering type label map for question headers
const RENDERING_LABELS = {
  text: 'Text',
  textarea: 'Text Area',
  number: 'Number',
  date: 'Date',
  checkbox: 'Checkbox',
  radio: 'Radio',
  select: 'Select',
  'checkbox-with-select': 'Checkbox+Select',
  group: 'Group',
  scale: 'Scale',
};


/**
 * FormInteractiveBuilder - Visual form builder with inline editing
 * OpenMRS O3 style - direct editing without modals
 * 
 * Features:
 * - Universal dependency editor (sections, questions can depend on anything)
 * - Collapsible questions using Ant Design Collapse
 * - Active page selector for performance with large multi-page forms
 * - Lazy section rendering via destroyInactivePanel
 */
const FormInteractiveBuilder = ({ schema, onSchemaChange, showPreview = true, formName }) => {
  const [editingPageIndex, setEditingPageIndex] = useState(null);
  const [activeBuilderPage, setActiveBuilderPage] = useState(0);
  const { token } = theme.useToken();

  useEffect(() => {
    notification.config({
      placement: 'bottom',
      bottom: 50,
      duration: 3
    });
  }, []);

  // Clamp active page to valid range when pages change
  useEffect(() => {
    const pageCount = schema?.pages?.length || 0;
    if (pageCount > 0 && activeBuilderPage >= pageCount) {
      setActiveBuilderPage(pageCount - 1);
    }
  }, [schema?.pages?.length, activeBuilderPage]);

  // Use refs to store latest values without causing re-renders
  const schemaRef = useRef(schema);
  const onSchemaChangeRef = useRef(onSchemaChange);
  
  useEffect(() => {
    schemaRef.current = schema;
  }, [schema]);
  
  useEffect(() => {
    onSchemaChangeRef.current = onSchemaChange;
  }, [onSchemaChange]);

  // Create stable updateSchema function that doesn't recreate on every render
  const updateSchema = useCallback(
    createUpdateSchema(schemaRef, onSchemaChangeRef),
    []
  );

  const updateQuestionField = useCallback(
    createUpdateQuestionField(updateSchema, getQuestion),
    [updateSchema]
  );

  const showNotification = useCallback((message, description) => {
    notification.success({ message, description });
  }, []);

  // Create all handlers
  const handlers = useMemo(
    () => createHandlers(updateSchema, updateQuestionField, showNotification),
    [updateSchema, updateQuestionField, showNotification]
  );

  const {
    handleAddPage,
    handleAddSection,
    handleAddQuestion,
    handleAddRichText,
    handleUpdateQuestion,
    handleDeleteQuestion,
    handleCloneQuestion,
    handleMoveSection,
    handleMoveQuestion,
    handleDeleteSection,
    handleCloneSection,
    handleDeletePage,
    handleAddAnswer,
    handleAddSeparator,
    handleAddLineBreak,
    handleMoveAnswer,
    handleDeleteAnswer,
    handleCloneAnswer,
    handleUpdateQuestionLayout,
  } = handlers;

  // --- Dependency update helper ---

  const handleQuestionDependencyUpdate = useCallback((pageIndex, sectionIndex, questionIndex, enableWhen, enableBehavior) => {
    updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
      if (enableWhen && enableWhen.length > 0) {
        q.enableWhen = enableWhen;
        q.enableBehavior = enableBehavior;
      } else {
        delete q.enableWhen;
        delete q.enableBehavior;
      }
    });
  }, [updateQuestionField]);

  const handleSectionDependencyUpdate = useCallback((pageIndex, sectionIndex, enableWhen, enableBehavior) => {
    updateSchema((newSchema) => {
      const section = getSection(newSchema, pageIndex, sectionIndex);
      if (!section) return;
      if (enableWhen && enableWhen.length > 0) {
        section.enableWhen = enableWhen;
        section.enableBehavior = enableBehavior;
      } else {
        delete section.enableWhen;
        delete section.enableBehavior;
      }
    });
  }, [updateSchema]);

  // Render question content (without Panel wrapper)
  const renderQuestionContent = (question, pageIndex, sectionIndex, questionIndex) => {
    const rendering = question.questionOptions?.rendering || 'text';
    const hasAnswers = ['checkbox', 'radio', 'select', 'checkbox-with-select'].includes(rendering);
    const questions = getQuestions(schema, pageIndex, sectionIndex);
    const questionsCount = questions?.length || 0;

    // Checkbox with Select composite component
    if (rendering === 'checkbox-with-select') {
      const hasCheckboxSelectItems = question.questionOptions?.answers && question.questionOptions.answers.length > 0;
      
      return (
        <div style={{ padding: 16 }}>
          <Row gutter={[16, 8]} align="middle" style={{ marginBottom: 16 }}>
            <Col flex="auto">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <DebouncedInput
                  size="large"
                  value={question.label}
                  onChange={(e) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'label', e.target.value)}
                  placeholder="Component Label"
                  addonBefore="Question Label"
                  style={{ fontWeight: 500 }}
                />
                <Row gutter={8}>
                  <Col span={8}>
                    <DebouncedInput
                      placeholder="Question ID"
                      value={question.id}
                      onChange={(e) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'id', e.target.value)}
                    />
                  </Col>
                  <Col span={8}>
                    <DebouncedInput
                      placeholder="Concept ID"
                      value={question.questionOptions?.concept || ''}
                      onChange={(e) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'concept', e.target.value)}
                    />
                  </Col>
                  <Col span={8}>
                    <Select
                      value={question.questionOptions?.layout || 'vertical'}
                      onChange={(value) => handleUpdateQuestionLayout(pageIndex, sectionIndex, questionIndex, value)}
                      style={{ width: '100%' }}
                      placeholder="Layout"
                    >
                      <Select.Option value="vertical">Vertical</Select.Option>
                      <Select.Option value="horizontal">Horizontal</Select.Option>
                    </Select>
                  </Col>
                  {question.questionOptions?.layout === 'horizontal' && (
                    <Col span={8}>
                      <Select
                        value={question.questionOptions?.columns || 'none'}
                        onChange={(value) => {
                          updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                            if (!q.questionOptions) q.questionOptions = {};
                            if (value === 'none') {
                              delete q.questionOptions.columns;
                            } else {
                              q.questionOptions.columns = parseInt(value, 10);
                            }
                          });
                        }}
                        style={{ width: '100%' }}
                        placeholder="Columns"
                      >
                        <Select.Option value="none">None</Select.Option>
                        <Select.Option value="1">1 Column</Select.Option>
                        <Select.Option value="2">2 Columns</Select.Option>
                        <Select.Option value="3">3 Columns</Select.Option>
                        <Select.Option value="4">4 Columns</Select.Option>
                      </Select>
                    </Col>
                  )}
                  <Col>
                    <Space>
                      <Text style={{ fontSize: 12 }}>Required:</Text>
                      <Switch
                        size="small"
                        checked={question.required === true}
                        onChange={(checked) => {
                          updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                            if (checked) {
                              q.required = true;
                            } else {
                              delete q.required;
                            }
                          });
                        }}
                      />
                    </Space>
                  </Col>
                </Row>
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0' }} />

          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {hasCheckboxSelectItems && (
              <div>
                <Text strong>Checkbox with Select Items</Text>
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={8}>
                  {question.questionOptions.answers.map((answer, answerIndex) => (
                    <CheckboxWithSelectItem
                      key={answerIndex}
                      answer={answer}
                      pageIndex={pageIndex}
                      sectionIndex={sectionIndex}
                      questionIndex={questionIndex}
                      answerIndex={answerIndex}
                      updateSchema={updateSchema}
                      handleCloneAnswer={handleCloneAnswer}
                      handleDeleteAnswer={handleDeleteAnswer}
                    />
                  ))}
                </Space>
              </div>
            )}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                updateSchema((newSchema) => {
                  const question = getQuestion(newSchema, pageIndex, sectionIndex, questionIndex);
                  if (question) {
                    if (!question.questionOptions) question.questionOptions = {};
                    if (!question.questionOptions.answers) question.questionOptions.answers = [];
                    question.questionOptions.answers.push({ ...DEFAULT_CHECKBOX_SELECT_ITEM });
                  }
                });
              }}
              block
              size="large"
            >
              Add Checkbox with Select Item
            </Button>

            <OtherOption
              question={question}
              pageIndex={pageIndex}
              sectionIndex={sectionIndex}
              questionIndex={questionIndex}
              updateQuestionField={updateQuestionField}
            />
          </Space>

          <Divider style={{ margin: '16px 0 8px' }} />
          <DependencyEditor
            conditions={question.enableWhen || []}
            behavior={question.enableBehavior || 'any'}
            onUpdate={(ew, eb) => handleQuestionDependencyUpdate(pageIndex, sectionIndex, questionIndex, ew, eb)}
            schema={schema}
            excludeQuestionId={question.id}
          />
        </div>
      );
    }

    // Rich Text type
    if (question.type === 'richtext') {
      return (
        <div style={{ padding: 16 }}>
          <Row gutter={[16, 8]} align="middle" style={{ marginBottom: 16 }}>
            <Col flex="auto">
              <DebouncedInput
                size="large"
                value={question.label}
                onChange={(e) => {
                  updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                    q.label = e.target.value;
                  });
                }}
                placeholder="Rich Text Label"
                addonBefore="Question Label"
                style={{ fontWeight: 500 }}
              />
            </Col>
          </Row>
          <RichContentEditor
            value={question.content || ''}
            onChange={(newContent) => {
              updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                q.content = newContent;
              });
            }}
            placeholder="Enter formatted text, instructions, or information here..."
            showMetadata={false}
          />

          <Divider style={{ margin: '16px 0 8px' }} />
          <DependencyEditor
            conditions={question.enableWhen || []}
            behavior={question.enableBehavior || 'any'}
            onUpdate={(ew, eb) => handleQuestionDependencyUpdate(pageIndex, sectionIndex, questionIndex, ew, eb)}
            schema={schema}
            excludeQuestionId={question.id}
          />
        </div>
      );
    }

    // Regular question
    return (
      <div style={{ padding: 16 }}>
        <Row gutter={[16, 8]} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <DebouncedInput
                size="large"
                value={question.label}
                onChange={(e) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'label', e.target.value)}
                placeholder="Question label"
                addonBefore="Question Label"
                style={{ fontWeight: 500 }}
              />
              <Row gutter={8}>
                <Col span={6}>
                  <DebouncedInput
                    placeholder="Question ID"
                    value={question.id}
                    onChange={(e) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'id', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  <Space.Compact style={{ width: '100%' }}>
                    <Select
                      value={rendering}
                      onChange={(value) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'rendering', value)}
                      style={{ width: PARAMETRABLE_FIELD_TYPES.includes(rendering) ? 'calc(100% - 32px)' : '100%' }}
                      placeholder="Type"
                    >
                      <Select.Option value="text">Text</Select.Option>
                      <Select.Option value="textarea">Text Area</Select.Option>
                      <Select.Option value="number">Number</Select.Option>
                      <Select.Option value="date">Date</Select.Option>
                      <Select.Option value="checkbox">Checkbox</Select.Option>
                      <Select.Option value="radio">Radio</Select.Option>
                      <Select.Option value="select">Select</Select.Option>
                      <Select.Option value="checkbox-with-select">Checkbox with Select</Select.Option>
                      <Select.Option value="group">Group</Select.Option>
                      <Select.Option value="scale">Scale (Slider)</Select.Option>
                      <Select.Option value="file">File Upload</Select.Option>
                    </Select>
                    {rendering === 'scale' && (
                      <ScaleSettingsPopover
                        config={question.questionOptions?.config || {}}
                        onChange={(newConfig) => updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                          if (!q.questionOptions) q.questionOptions = {};
                          q.questionOptions.config = newConfig;
                        })}
                      />
                    )}
                  </Space.Compact>
                </Col>
                <Col span={6}>
                  <DebouncedInput
                    placeholder="Concept ID"
                    value={question.questionOptions?.concept || ''}
                    onChange={(e) => handleUpdateQuestion(pageIndex, sectionIndex, questionIndex, 'concept', e.target.value)}
                  />
                </Col>
                <Col span={6}>
                  <Select
                    value={question.questionOptions?.layout || 'vertical'}
                    onChange={(value) => handleUpdateQuestionLayout(pageIndex, sectionIndex, questionIndex, value)}
                    style={{ width: '100%' }}
                    placeholder="Layout"
                  >
                    <Select.Option value="vertical">Vertical</Select.Option>
                    <Select.Option value="horizontal">Horizontal</Select.Option>
                  </Select>
                </Col>
                {question.questionOptions?.layout === 'horizontal' && (
                  <Col span={6}>
                    <Select
                      value={question.questionOptions?.columns || 'none'}
                      onChange={(value) => {
                        updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                          if (!q.questionOptions) q.questionOptions = {};
                          if (value === 'none') {
                            delete q.questionOptions.columns;
                          } else {
                            q.questionOptions.columns = parseInt(value, 10);
                          }
                        });
                      }}
                      style={{ width: '100%' }}
                      placeholder="Columns"
                    >
                      <Select.Option value="none">None</Select.Option>
                      <Select.Option value="1">1 Column</Select.Option>
                      <Select.Option value="2">2 Columns</Select.Option>
                      <Select.Option value="3">3 Columns</Select.Option>
                      <Select.Option value="4">4 Columns</Select.Option>
                    </Select>
                  </Col>
                )}
                <Col>
                  <Space>
                    <Text style={{ fontSize: 12 }}>Show question label in preview:</Text>
                    <Switch
                      size="small"
                      checked={question.showInPreview !== false}
                      onChange={(checked) => {
                        updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                          if (checked) {
                            delete q.showInPreview;
                          } else {
                            q.showInPreview = false;
                          }
                        });
                      }}
                    />
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Text style={{ fontSize: 12 }}>Required:</Text>
                    <Switch
                      size="small"
                      checked={question.required === true}
                      onChange={(checked) => {
                        updateQuestionField(pageIndex, sectionIndex, questionIndex, (q) => {
                          if (checked) {
                            q.required = true;
                          } else {
                            delete q.required;
                          }
                        });
                      }}
                    />
                  </Space>
                </Col>
              </Row>
            </Space>
          </Col>
        </Row>
        
        {hasAnswers && (
          <div style={{ marginTop: 16, paddingTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <OtherOption
                question={question}
                pageIndex={pageIndex}
                sectionIndex={sectionIndex}
                questionIndex={questionIndex}
                updateQuestionField={updateQuestionField}
              />
              {question.questionOptions?.answers?.map((answer, answerIndex) => (
                <AnswerEditor
                  key={answerIndex}
                  answer={answer}
                  index={answerIndex}
                  total={question.questionOptions.answers.length}
                  getNode={(s) => getAnswer(s, pageIndex, sectionIndex, questionIndex, answerIndex)}
                  updateSchema={updateSchema}
                  onDelete={() => handleDeleteAnswer(pageIndex, sectionIndex, questionIndex, answerIndex)}
                  onClone={() => handleCloneAnswer(pageIndex, sectionIndex, questionIndex, answerIndex)}
                  onMove={(dir) => handleMoveAnswer(pageIndex, sectionIndex, questionIndex, answerIndex, dir)}
                  allSiblings={question.questionOptions?.answers || []}
                />
              ))}
              <Space style={{ width: '100%' }} wrap>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddAnswer(pageIndex, sectionIndex, questionIndex)}
                  size="large"
                >
                  Add Answer
                </Button>
                <Button
                  type="text"
                  size="small"
                  onClick={() => handleAddSeparator(pageIndex, sectionIndex, questionIndex)}
                  style={{ color: '#8c8c8c' }}
                >
                  ── Separator
                </Button>
                <Button
                  type="text"
                  size="small"
                  onClick={() => handleAddLineBreak(pageIndex, sectionIndex, questionIndex)}
                  style={{ color: '#8c8c8c' }}
                >
                  ↵ Line Break
                </Button>
              </Space>
            </Space>
          </div>
        )}
        
        <Divider style={{ margin: '16px 0 8px' }} />
        <DependencyEditor
          conditions={question.enableWhen || []}
          behavior={question.enableBehavior || 'any'}
          onUpdate={(ew, eb) => handleQuestionDependencyUpdate(pageIndex, sectionIndex, questionIndex, ew, eb)}
          schema={schema}
          excludeQuestionId={question.id}
        />
        
        {question.questions && question.questions.length > 0 && (
          <div style={{ marginTop: 16, paddingLeft: 16 }}>
            {question.questions.map((subQ, subIndex) => 
              renderQuestion(subQ, pageIndex, sectionIndex, `${questionIndex}-${subIndex}`)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render question wrapped in Collapse Panel
  const renderQuestion = (question, pageIndex, sectionIndex, questionIndex) => {
    const rendering = question.questionOptions?.rendering || 'text';
    const questions = getQuestions(schema, pageIndex, sectionIndex);
    const questionsCount = questions?.length || 0;
    const answerCount = question.questionOptions?.answers?.length || 0;
    const hasEnableWhen = question.enableWhen?.length > 0;
    const questionKey = question.id || `q_${pageIndex}_${sectionIndex}_${questionIndex}`;

    const questionHeader = (
      <Space>
        <Text strong>{question.label || 'Untitled Question'}</Text>
        <Tag color="blue" style={{ fontSize: 10 }}>
          {question.type === 'richtext' ? 'Rich Text' : RENDERING_LABELS[rendering] || rendering}
        </Tag>
        {answerCount > 0 && (
          <Tag style={{ fontSize: 10 }}>{answerCount} answers</Tag>
        )}
        {hasEnableWhen && (
          <Tag color="orange" style={{ fontSize: 10 }}>Conditional</Tag>
        )}
        {question.required && (
          <Tag color="red" style={{ fontSize: 10 }}>Required</Tag>
        )}
      </Space>
    );

    const questionExtra = (
      <Space onClick={(e) => e.stopPropagation()}>
        <Button
          type="text"
          size="small"
          icon={<UpOutlined />}
          onClick={() => handleMoveQuestion(pageIndex, sectionIndex, questionIndex, 'up')}
          disabled={questionIndex === 0}
          style={{ opacity: questionIndex === 0 ? 0.3 : 1 }}
        />
        <Button
          type="text"
          size="small"
          icon={<DownOutlined />}
          onClick={() => handleMoveQuestion(pageIndex, sectionIndex, questionIndex, 'down')}
          disabled={questionIndex === questionsCount - 1}
          style={{ opacity: questionIndex === questionsCount - 1 ? 0.3 : 1 }}
        />
        <ActionButtons
          onClone={() => handleCloneQuestion(pageIndex, sectionIndex, questionIndex)}
          onDelete={() => handleDeleteQuestion(pageIndex, sectionIndex, questionIndex)}
          cloneTitle="Clone question"
        />
      </Space>
    );

    return (
      <Panel
        key={questionKey}
        header={questionHeader}
        extra={questionExtra}
        style={{ marginBottom: 8 }}
      >
        {renderQuestionContent(question, pageIndex, sectionIndex, questionIndex)}
      </Panel>
    );
  };

  // --- Page navigation for performance ---
  const pages = schema?.pages || [];
  const pageCount = pages.length;
  const activePage = pages[activeBuilderPage];

  const builderContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${token.colorBorder}`, flexShrink: 0 }}>
        <div style={{ marginBottom: 8 }}>
          <Title level={4} style={{ margin: 0, marginBottom: 4 }}>Form Builder</Title>
          <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
            Build your form structure. Changes are instantly reflected in the live preview.
          </Paragraph>
        </div>
        <Space wrap>
          <Button type="link" icon={<PlusOutlined />} onClick={handleAddPage} size="large" style={{ padding: 0 }}>
            Add Page
          </Button>
          {pageCount > 1 && (
            <Select
              value={activeBuilderPage}
              onChange={setActiveBuilderPage}
              style={{ minWidth: 200 }}
              size="small"
            >
              {pages.map((p, i) => (
                <Select.Option key={i} value={i}>
                  {p.label || `Page ${i + 1}`}
                </Select.Option>
              ))}
            </Select>
          )}
        </Space>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, paddingBottom: 120 }}>
        {activePage ? (
          <div style={{ marginBottom: 32 }}>
            <div style={{ padding: '16px 24px', borderRadius: '8px 8px 0 0' }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  {editingPageIndex === activeBuilderPage ? (
                    <Space>
                      <DebouncedInput
                        size="large"
                        value={activePage.label}
                        onChange={(e) => {
                          updateSchema((newSchema) => {
                            const p = newSchema.pages?.[activeBuilderPage];
                            if (p) p.label = e.target.value;
                          });
                        }}
                        onPressEnter={() => setEditingPageIndex(null)}
                        autoFocus
                        style={{ width: 300, fontWeight: 600 }}
                      />
                      <Button icon={<SaveOutlined />} onClick={() => setEditingPageIndex(null)}>
                        Save
                      </Button>
                    </Space>
                  ) : (
                    <Space>
                      <Title level={5} style={{ margin: 0 }}>
                        {activePage.label}
                        {pageCount > 1 && (
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                            (Page {activeBuilderPage + 1} of {pageCount})
                          </Text>
                        )}
                      </Title>
                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditingPageIndex(activeBuilderPage)} />
                    </Space>
                  )}
                </Col>
                <Col>
                  <Button type="default" icon={<DeleteOutlined />} onClick={() => handleDeletePage(activeBuilderPage)} danger />
                </Col>
              </Row>
            </div>

            <div style={{ padding: 24, borderRadius: '0 0 8px 8px' }}>
              <Paragraph type="secondary">
                Below are the sections linked to this page. Expand each section to add questions to it.
              </Paragraph>

              {activePage.freeText !== undefined ? (
                <div style={{ marginBottom: 24 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong>Free Text (Outside Sections)</Text>
                      {renderDeleteButton(() => {
                        updateSchema((newSchema) => {
                          const p = newSchema.pages?.[activeBuilderPage];
                          if (p) delete p.freeText;
                        });
                      }, 'small')}
                    </div>
                    <RichContentEditor
                      value={activePage.freeText || ''}
                      onChange={(content) => {
                        updateSchema((newSchema) => {
                          const p = newSchema.pages?.[activeBuilderPage];
                          if (p) p.freeText = content;
                        });
                      }}
                      placeholder="Enter free text content that appears outside sections..."
                      height="150px"
                      showMetadata={false}
                    />
                  </Space>
                </div>
              ) : (
                <div style={{ marginBottom: 24 }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      updateSchema((newSchema) => {
                        const p = newSchema.pages?.[activeBuilderPage];
                        if (p) p.freeText = '<p>Enter your free text content here...</p>';
                      });
                    }}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    Add Free Text (Outside Sections)
                  </Button>
                </div>
              )}

              <Collapse
                bordered={false}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                style={{ background: token.colorBgContainer }}
                items={activePage.sections?.map((section, sectionIndex) => {
                  const sectionQuestions = section.questions || [];
                  const hasSectionDeps = section.enableWhen?.length > 0;
                  const panelStyle = {
                    marginBottom: 24,
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadiusLG,
                    border: 'none',
                  };

                  return {
                    key: String(sectionIndex),
                    label: (
                      <Space>
                        {section.label}
                        {hasSectionDeps && <Tag color="orange" style={{ fontSize: 10 }}>Conditional</Tag>}
                        <Tag style={{ fontSize: 10 }}>{sectionQuestions.length} questions</Tag>
                      </Space>
                    ),
                    extra: (
                      <Space onClick={(e) => e.stopPropagation()}>
                        <Button
                          type="text"
                          size="small"
                          icon={<UpOutlined />}
                          onClick={() => handleMoveSection(activeBuilderPage, sectionIndex, 'up')}
                          disabled={sectionIndex === 0}
                          style={{ opacity: sectionIndex === 0 ? 0.3 : 1 }}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<DownOutlined />}
                          onClick={() => handleMoveSection(activeBuilderPage, sectionIndex, 'down')}
                          disabled={sectionIndex === activePage.sections.length - 1}
                          style={{ opacity: sectionIndex === activePage.sections.length - 1 ? 0.3 : 1 }}
                        />
                        <ActionButtons
                          onClone={() => handleCloneSection(activeBuilderPage, sectionIndex)}
                          onDelete={() => handleDeleteSection(activeBuilderPage, sectionIndex)}
                          size="small"
                          cloneTitle="Clone section"
                        />
                      </Space>
                    ),
                    children: (
                      <>
                        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size={12}>
                          <DebouncedInput
                            size="large"
                            value={section.label}
                            onChange={(e) => {
                              updateSchema((newSchema) => {
                                const s = newSchema.pages?.[activeBuilderPage]?.sections?.[sectionIndex];
                                if (s) s.label = e.target.value;
                              });
                            }}
                            placeholder="Section label"
                            addonBefore="Section Label"
                          />

                          <Row gutter={16} align="middle">
                            <Col span={12}>
                              <Space>
                                <Text>Column Layout:</Text>
                                <InputNumber
                                  min={1}
                                  max={4}
                                  value={section.columnLayout || 1}
                                  onChange={(value) => {
                                    updateSchema((newSchema) => {
                                      const s = newSchema.pages?.[activeBuilderPage]?.sections?.[sectionIndex];
                                      if (s) s.columnLayout = value;
                                    });
                                  }}
                                  size="small"
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  ({section.columnLayout || 1} column{(section.columnLayout || 1) > 1 ? 's' : ''})
                                </Text>
                              </Space>
                            </Col>
                            <Col span={12}>
                              <Space>
                                <Text>Show Title in Preview:</Text>
                                <Switch
                                  checked={section.showTitleInPreview !== false}
                                  onChange={(checked) => {
                                    updateSchema((newSchema) => {
                                      const s = newSchema.pages?.[activeBuilderPage]?.sections?.[sectionIndex];
                                      if (s) s.showTitleInPreview = checked;
                                    });
                                  }}
                                  size="small"
                                />
                              </Space>
                            </Col>
                          </Row>

                          <DependencyEditor
                            conditions={section.enableWhen || []}
                            behavior={section.enableBehavior || 'any'}
                            onUpdate={(ew, eb) => handleSectionDependencyUpdate(activeBuilderPage, sectionIndex, ew, eb)}
                            schema={schema}
                            label="Section Conditional Display"
                          />
                        </Space>

                        <Divider />

                        <Collapse bordered={false} destroyInactivePanel style={{ marginTop: 16 }}>
                          {sectionQuestions.map((question, questionIndex) => 
                            renderQuestion(question, activeBuilderPage, sectionIndex, questionIndex)
                          )}
                        </Collapse>

                        <Space style={{ width: '100%', marginTop: 16 }} wrap>
                          <Button type="dashed" icon={<PlusOutlined />} onClick={() => handleAddQuestion(activeBuilderPage, sectionIndex)} size="large">
                            Add Question
                          </Button>
                          <Button type="dashed" icon={<PlusOutlined />} onClick={() => handleAddRichText(activeBuilderPage, sectionIndex)} size="large">
                            Add Text Section
                          </Button>
                        </Space>
                      </>
                    ),
                    style: panelStyle,
                  };
                })}
              />

              <Button type="link" icon={<PlusOutlined />} onClick={() => handleAddSection(activeBuilderPage)} size="large" style={{ marginTop: 16 }}>
                Add Section
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Text type="secondary">No pages yet. Click "Add Page" to get started.</Text>
          </div>
        )}
      </div>
    </div>
  );

  if (!showPreview) {
    return builderContent;
  }

  return (
    <Splitter style={{ height: '100%', display: 'flex', flexDirection: 'row' }}>
      <Splitter.Panel defaultSize="50%" min="40%" max="70%">
        <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
          {builderContent}
        </div>
      </Splitter.Panel>
      
      <Splitter.Panel defaultSize="50%">
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ 
            padding: '12px 16px', 
            borderBottom: `1px solid ${token.colorBorder}`,
            backgroundColor: token.colorBgContainer,
            fontWeight: 500,
            flexShrink: 0,
            zIndex: 10
          }}>
            Live Preview
          </div>
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px', minHeight: 0 }}>
            <FormPreview schema={schema} formName={formName} />
          </div>
        </div>
      </Splitter.Panel>
    </Splitter>
  );
};

export default FormInteractiveBuilder;
